/**
 * Stale-write protection for the board's persisted allocation edits.
 *
 * WHY THIS EXISTS: `PUT /allocations` is delete-and-rewrite over the WHOLE day window
 * (backend/src/update-allocations.ts) and carries no version/etag. The board builds that body from the
 * allocations array it happens to be holding, so two runners editing the same day silently clobber each
 * other: A's tab loaded at 07:00, B hands OR-7 to a different doctor at 07:05, A makes an unrelated move
 * at 07:06 and the PUT recreates A's stale snapshot — B's change is gone with no error anywhere.
 *
 * `doctorMoves.ts` already refuses the catastrophic writes (list not loaded / empty / no-op). This module
 * covers the remaining case: the snapshot is fine, but the SERVER has moved on since it was taken.
 *
 * The strategy is re-read-then-rebase, not lock:
 *   1. re-fetch the window immediately before writing;
 *   2. if a schedule THIS edit touches changed server-side, refuse — the two edits genuinely conflict and
 *      silently picking a winner is exactly the bug;
 *   3. otherwise rebuild the payload from the FRESH list with the edits re-applied, so a concurrent edit
 *      to some OTHER room survives instead of being rewritten away.
 *
 * Kept as a pure, separately-tested module (like doctorMoves.ts / recolor.ts) so no caller can quietly
 * reintroduce the unguarded write.
 */

/** Minimal allocation shape; the board's, the Anesthesiologists tab's and the CRNA tab's types all satisfy it. */
export interface GuardableAllocation {
  operatingRoomScheduleId: string;
  allocatedNurseIds?: string[];
  allocatedDoctorIds?: string[];
}

/** Shown to the runner when a write is refused because someone else changed the day. */
export const STALE_WRITE_MESSAGE =
  'Someone else changed this day while you were editing. Your change was NOT saved — reload to see the latest board, then try again.';

/** Shown when the server rejected or never received the write. */
export const FAILED_WRITE_MESSAGE = 'Failed to save the change — the board has been put back to the last saved state.';

/** Assignment lists are compared as SETS: the server is free to return ids in any order. */
const sameIdSet = (a: string[] = [], b: string[] = []): boolean => {
  if (a.length !== b.length) return false;
  const x = [...a].sort();
  const y = [...b].sort();
  return x.every((v, i) => v === y[i]);
};

/** The schedules an edit list touches. Both DoctorEdit and NurseEdit carry `scheduleId`. */
export function editedScheduleIds(edits: { scheduleId: string }[]): string[] {
  return Array.from(new Set(edits.map(e => e.scheduleId)));
}

/**
 * Of `scheduleIds`, the ones whose SERVER state no longer matches the snapshot the edit was built from.
 * A schedule that gained or lost its allocation entirely counts as conflicting.
 */
export function conflictingScheduleIds(
  snapshot: GuardableAllocation[],
  fresh: GuardableAllocation[],
  scheduleIds: string[],
): string[] {
  const before = new Map(snapshot.map(a => [a.operatingRoomScheduleId, a]));
  const after = new Map(fresh.map(a => [a.operatingRoomScheduleId, a]));
  const conflicts: string[] = [];
  for (const id of scheduleIds) {
    const b = before.get(id);
    const a = after.get(id);
    if (!b && !a) continue;                       // untouched on both sides
    if (!b || !a) { conflicts.push(id); continue; } // appeared or disappeared server-side
    if (!sameIdSet(b.allocatedDoctorIds, a.allocatedDoctorIds) ||
        !sameIdSet(b.allocatedNurseIds, a.allocatedNurseIds)) conflicts.push(id);
  }
  return conflicts;
}

export type CommitOutcome<P> =
  | { ok: true; payload: P[]; fresh: GuardableAllocation[] }
  | { ok: false; reason: 'conflict'; message: string; conflicts: string[] }
  | { ok: false; reason: 'noop'; message: null }
  | { ok: false; reason: 'error'; message: string; error: unknown };

/**
 * Re-read the window, refuse on a true conflict, otherwise rebase the edit onto fresh server state and write.
 *
 * `rebuild` is the existing payload builder (buildDoctorEditPayload / buildNurseEditPayload) applied to the
 * FRESH list — returning null means the edit became a no-op against current server state, which is a
 * success-shaped "nothing to do", not a failure.
 *
 * The caller keeps ownership of optimistic UI: on any non-ok outcome it must restore the pre-edit state.
 */
export async function commitAllocationEdit<P>(opts: {
  snapshot: GuardableAllocation[];
  editedIds: string[];
  fetchFresh: () => Promise<GuardableAllocation[]>;
  rebuild: (fresh: GuardableAllocation[]) => { payload: P[] } | null;
  put: (payload: P[]) => Promise<unknown>;
}): Promise<CommitOutcome<P>> {
  const { snapshot, editedIds, fetchFresh, rebuild, put } = opts;
  let fresh: GuardableAllocation[];
  try {
    fresh = await fetchFresh();
  } catch (error) {
    return { ok: false, reason: 'error', message: FAILED_WRITE_MESSAGE, error };
  }

  const conflicts = conflictingScheduleIds(snapshot, fresh, editedIds);
  if (conflicts.length) return { ok: false, reason: 'conflict', message: STALE_WRITE_MESSAGE, conflicts };

  const rebased = rebuild(fresh);
  if (!rebased) return { ok: false, reason: 'noop', message: null };

  try {
    await put(rebased.payload);
  } catch (error) {
    return { ok: false, reason: 'error', message: FAILED_WRITE_MESSAGE, error };
  }
  return { ok: true, payload: rebased.payload, fresh };
}
